
import { defineComponent, PropType } from 'vue';
import { useApplicationStore } from '@/store/applicationStore';
import type { ColumnDef } from '@/types/ColumnDef';
import type { ProformaMultiplierItem, ProformaLine, ProformaCurrency, ProformaInfo } from '@/types/ProformaTypes';
import { getSubTotalValue } from '../utils/proforma-utils';

export default defineComponent({
  props: {
    lines: { type: Array as PropType<ProformaLine[]>, required: true },
    currency: { type: Array as PropType<ProformaCurrency[]>, required: true },
    proformaInfo: { type: Object as PropType<ProformaInfo>, required: true },
    LOCAL_CURRENCY: { type: String },
    readOnly: { type: Boolean, default: true },
  },
  emits: ['line-updated'],
  setup() {
    const applicationStore = useApplicationStore();
    return { applicationStore, getSubTotalValue };
  },
  data() {
    return {
      multiplierDialog: {
        items: [] as ProformaMultiplierItem[],
        showDialog: false,
        selectedMultiplierIds: [] as number[],
        proformaLineID: 0 as number,
      },
      focusedInputs: [] as string[],
      columnSize: '70px' as string,
    };
  },
  methods: {
    handleOnInpuFocused(label: string, isFocused: boolean = true) {
      const idx = this.focusedInputs.findIndex((x) => x === label);
      if (isFocused && idx === -1) {
        this.focusedInputs.push(label);
      }
      if (!isFocused) {
        this.focusedInputs = this.focusedInputs.filter((x) => x !== label);
      }
    },
    onMultiplierClick(multiplierItems: ProformaMultiplierItem[], proformaLineID: number) {
      this.multiplierDialog.items = multiplierItems;
      this.multiplierDialog.showDialog = true;
      this.multiplierDialog.proformaLineID = proformaLineID;
      this.multiplierDialog.selectedMultiplierIds = [];
    },
    onMultiplierRowClick(multiplierItem: ProformaMultiplierItem) {
      const idx = this.multiplierDialog.selectedMultiplierIds.findIndex((i) => i == multiplierItem.ID);
      if (idx >= 0) {
        this.multiplierDialog.selectedMultiplierIds.splice(idx, 1);
      } else {
        this.multiplierDialog.selectedMultiplierIds.push(multiplierItem.ID);
      }
    },
    onDialogOkClick() {
      let multiplierSum = 0;
      this.multiplierDialog.selectedMultiplierIds.forEach((multiplierID) => {
        const value = this.multiplierDialog.items.find((x) => x.ID === multiplierID)?.VALUE ?? 0;
        multiplierSum += value;
      });
      const proformaLine = this.lines.find((x) => x.ID === this.multiplierDialog.proformaLineID);
      if (proformaLine) {
        const newProformaLine: ProformaLine = { ...proformaLine, MULTIPLE: multiplierSum };
        this.$emit('line-updated', {
          line: newProformaLine,
          field: 'MULTIPLE',
        });
      }
    },

    onQuantityChange(proformaLine: ProformaLine, inputValue: string) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      const newProformaLine: ProformaLine = { ...proformaLine, QUANTITY_IN: value };

      this.$emit('line-updated', {
        line: newProformaLine,
        field: 'QUANTITY_IN',
      });
    },
    onRateChange(proformaLine: ProformaLine, inputValue: string) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      const newProformaLine: ProformaLine = { ...proformaLine, RATE: value };
      this.$emit('line-updated', {
        line: newProformaLine,
        field: 'RATE',
      });
    },
    onMultiplierChange(proformaLine: ProformaLine, inputValue: string) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      const newProformaLine: ProformaLine = { ...proformaLine, MULTIPLE: value };

      this.$emit('line-updated', {
        line: newProformaLine,
        field: 'MULTIPLE',
      });
    },

    onRebatePercentChange(proformaLine: ProformaLine, inputValue: string) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      if (value) {
        const newProformaLine: ProformaLine = { ...proformaLine, REBATE_IN: value, REBATE_AMOUNT: undefined };

        this.$emit('line-updated', {
          line: newProformaLine as ProformaLine,
          field: 'REBATE_IN',
        });
      }
    },
    onRebateAmountChange(inputValue: string, proformaLine: ProformaLine) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      console.log(value, proformaLine.REBATE_AMOUNT);
      if (value !== proformaLine.REBATE_AMOUNT) {
        const newProformaLine: ProformaLine = { ...proformaLine, REBATE_AMOUNT: value, REBATE_IN: undefined };

        this.$emit('line-updated', {
          line: newProformaLine,
          field: 'REBATE_AMOUNT',
        });
      }
    },
    onVatPercentChange(proformaLine: ProformaLine, inputValue: string) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      const newProformaLine = { ...proformaLine, VAT_PERCENT: value };
      this.$emit('line-updated', {
        line: newProformaLine,
        field: 'VAT_PERCENT',
      });
    },

    getSharedInputProps(label: string, showAsReadonly: boolean = false) {
      const readonly = this.readOnly;
      let backgroundColor: string | undefined;
      if (showAsReadonly) {
        backgroundColor = this.$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4';
      }
      const hideDetails = this.focusedInputs.includes(label) ? true : 'auto';
      const noflex = true;
      const dense = true;
      const disabled = showAsReadonly;
      return {
        readonly,
        backgroundColor,
        hideDetails,
        noflex,
        dense,
        solo: true,
        flat: true,
        outlined: false,
        disabled,
      };
    },
  },
  computed: {
    columns() {
      const result: ColumnDef<ProformaLine & { SubTotal: string }>[] = [
        { header: 'Description', width: '225px', field: 'PRICE_TEXT', align: 'start' },
        {
          header: 'QTY',
          width: '80px',
          field: 'QUANTITY_IN',
          dataType: 'currency',
          decimals: 2,
          align: 'start',
        },
        { header: 'Unit', width: '120px', field: 'QUANTITY_LABEL', align: 'start' },
        {
          header: 'Price',
          width: '130px',
          field: 'RATE',
          dataType: 'currency',
          decimals: 2,
          align: 'start',
        },
        { header: 'Currency', width: '60px', field: 'CURRENCY_CODE', align: 'start' },
        {
          header: 'Multiplier',
          width: '90px',
          field: 'MULTIPLE',
        },
        {
          header: 'Sub total',
          width: '130px',
          field: 'SubTotal',
          align: 'start',
          dataType: 'currency',
          decimals: 2,
        },
        {
          header: '+/- %',
          width: '100px',
          align: 'start',
          dataType: 'currency',
          field: 'REBATE_IN',
          decimals: 2,
        },
        {
          header: '+/- sum',
          width: '130px',
          dataType: 'currency',
          align: 'start',
          field: 'REBATE_AMOUNT',
          decimals: 2,
        },
        {
          header: 'VAT %',
          width: '100px',
          field: 'VAT_PERCENT',
        },
        {
          header: 'VAT',
          width: '120px',
          dataType: 'currency',
          field: 'VAT',
          align: 'start',
          decimals: 2,
        },
        {
          header: `Total ${this.LOCAL_CURRENCY}`,
          dataType: 'currency',
          width: '140px',
          field: 'AMOUNT_IN',
          align: 'start',
          decimals: 2,
          footerFunction: { fn: 'sum' },
        },
      ];
      if (this.currency) {
        this.currency
          .filter((i) => i.PER_SHOW === 1)
          .forEach((element, index) => {
            const field = `CUR${index + 1}`;
            if (field === 'CUR1' || field === 'CUR2' || field === 'CUR3' || field === 'CUR4') {
              result.push({
                header: `Total ${element.CURRENCY_NAME}`,
                field,
                dataType: 'currency',
                width: '140px',
                align: 'start',
                decimals: 2,
                footerFunction: { fn: 'sum' },
              });
            }
          });
      }
      result.push(
        { header: 'Comments', width: '150px', field: 'COMMENTS', dataType: 'string' },
        { header: 'Tariff description', width: '150px', nowrap: true, field: 'TARIFF_DESCRIPTION', dataType: 'string' }
      );
      // Set default header properties.
      return result.map((x: any) => ({ ...x, unSortable: true }));
    },
    multipliercolumns() {
      const result: any = [
        {
          header: 'Description',
          field: 'TEXT',
        },
        {
          header: 'Multiplier',
          field: 'VALUE',
        },
      ];

      return result;
    },
  },
});
